import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import { AttributeContainer, AttributeName, AttributeValue, CrossContainer, HeaderContainer, InputsContainer } from './styles';
import { CircularProgress, FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import theme from '../../theme/theme';
import { AddCircle, Close, Edit } from '@mui/icons-material';
import { getCategoriesList } from '../../services/categories/getCategories';
import { UploadFile } from '../../components/UploadFile';
import { postCategory, processCategoryImage } from '../../services/categories/postCategories';
import { toast } from 'react-toastify';
import { RequestModal } from '../../components/RequestModal';
import { postResourceType } from '../../services/resourceTypes/postResourceTypes';
import { TypeActionsProps } from './types';
import { putResourceType } from '../../services/resourceTypes/putResourceType';
import { useNavigate } from "react-router-dom";
import { ROUTES } from '../../routes/constants';

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    minWidth: '380px',
    maxHeight: '700px',
    overflow: 'auto',
    borderRadius: '20px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
};

const TypeActions: React.FC<TypeActionsProps> = ({ action, typeObj, onResponse }) => {
    // ------------------- JWT -------------------
    const jwt = localStorage.getItem('jwt');
    const userType = localStorage.getItem('user_type');

    const navigate = useNavigate();

    // ------ Modal ------
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setIsUploaded(false);
        setIsInvalid(false);
        if (action !== 'edit') {
            setType('');
            setCategory('');
        }
    }

    // ------ States ------
    const [type, setType] = React.useState(action === 'create' ? '' : typeObj.name);
    const [category, setCategory] = React.useState(action === 'create' ? '' : typeObj.resource_category);
    const [categories, setCategories] = React.useState<any>([]);

    const [isUploaded, setIsUploaded] = React.useState(false);
    const [isInvalid, setIsInvalid] = React.useState(false);
    const [alertTitle, setAlertTitle] = React.useState("");
    const [alertMessage, setAlertMessage] = React.useState("");

    const [loadingData, setLoadingData] = React.useState(false);
    const [loadingAction, setLoadingAction] = React.useState(false);

    const [typeError, setTypeError] = React.useState(false);
    const [categoryError, setCategoryError] = React.useState(false);

    // ------ Handlers ------
    const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setType(event.target.value);
    };

    const handleTypeBlur = () => {
        if (!type) {
            setTypeError(true);
        } else {
            setTypeError(false);
        }
    };

    const handleCategoryChange = (event: SelectChangeEvent) => {
        setCategory(event.target.value as string);
    };

    const handleCategoryBlur = () => {
        if (!category) {
            setCategoryError(true);
        } else {
            setCategoryError(false);
        }
    };

    // ------ Requests ------
    const getData = async () => {
        await getCategoriesList(jwt!)
            .then((response) => {
                setCategories(response.data);

                response.data.find((cat: any) => {
                    if (cat.name === typeObj.category) {
                        setCategory(cat.id);
                    }
                })
            })
            .catch((error) => {
                console.log(error);
            });
    }

    const manageType = async () => {
        if (!type || !category) {
            setAlertTitle("Campos inválidos");
            setAlertMessage("Debe completar todos los campos. Por favor, inténtelo de nuevo.");
            setIsInvalid(true);
        } else {
            const resourceType = {
                name: type,
                resource_category: category
            }
            if (action === 'create') {
                setLoadingAction(true);
                await postResourceType(jwt!, resourceType)
                    .then((response) => {
                        if (response.status === 201) {
                            setAlertTitle("Tipo Creado");
                            setAlertMessage("El tipo se creó correctamente.");
                            setIsUploaded(true);
                            onResponse && onResponse();
                        } else {
                            setAlertTitle("Ocurrió un error");
                            setAlertMessage("Ocurrió un error al crear el tipo. Inténtelo de nuevo.");
                            setIsInvalid(true);
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                        toast.error("Error al crear el tipo.");
                    });

                setLoadingAction(false);
            } else if (action === 'edit') {
                setLoadingAction(true);
                await putResourceType(jwt!, typeObj.id, resourceType)
                    .then((response) => {
                        if (response.status === 200) {
                            setAlertTitle("Tipo Modificado");
                            setAlertMessage("El tipo se modificó correctamente.");
                            setIsUploaded(true);
                            onResponse && onResponse();
                        } else {
                            setAlertTitle("Ocurrió un error");
                            setAlertMessage("Ocurrió un error al crear el tipo. Inténtelo de nuevo.");
                            setIsInvalid(true);
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                        toast.error("Error al crear el tipo.");
                    });

                setLoadingAction(false);
            }
        }
    }

    // ------ Effects ------
    React.useEffect(() => {
        setLoadingData(true);
        getData();
        setLoadingData(false);
    }, [])

    return (
        <div>
            {
                isUploaded ?
                    (
                        <RequestModal
                            title={alertTitle}
                            description={alertMessage}
                            onConfirm={handleClose}
                        />
                    ) : (
                        isInvalid &&
                        <RequestModal
                            title={alertTitle}
                            description={alertMessage}
                            onConfirm={(!category || !type) ? () => { setIsInvalid(false) } : handleClose}
                        />
                    )
            }
            <Button onClick={handleOpen} style={{ padding: '0px' }} >
                {action === 'create' ?
                    <AddCircle sx={{ color: theme.color.navy, fontSize: '2rem' }} />
                    :
                    <Edit sx={{ color: theme.color.navy, fontSize: '2rem' }} />
                }
            </Button>
            <Modal
                open={open}
                // onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    {
                        loadingData &&
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <CircularProgress sx={{ color: theme.color.navy }} />
                        </div>
                    }
                    {
                        loadingAction ?
                            (<div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <CircularProgress sx={{ color: theme.color.navy }} />
                            </div>
                            ) : (
                                <>
                                    <CrossContainer
                                        onClick={handleClose}
                                    >
                                        <Close />
                                    </CrossContainer>
                                    <HeaderContainer>
                                        {
                                            action === 'create' ?
                                                "Agregar nuevo tipo de recurso"
                                                :
                                                "Editar tipo de recurso"
                                        }
                                    </HeaderContainer>
                                    <InputsContainer>
                                        <AttributeContainer>
                                            <TextField
                                                label="Nombre de tipo"
                                                type="text"
                                                variant="outlined"
                                                fullWidth
                                                required
                                                sx={{ margin: "15px 0", width: '100%' }}
                                                InputProps={{
                                                    style: {
                                                        borderRadius: "14px",
                                                    },
                                                }}
                                                value={type}
                                                onChange={handleTypeChange}
                                                onBlur={handleTypeBlur}
                                                error={typeError}
                                                helperText={typeError ? 'Este campo es obligatorio' : ''}
                                            />
                                        </AttributeContainer>
                                        <FormControl sx={{ margin: "15px 0", width: '100%' }}>
                                            <InputLabel id="demo-simple-select-autowidth-label">Categorías</InputLabel>
                                            <Select
                                                labelId="demo-simple-select-autowidth-label"
                                                id="demo-simple-select-autowidth"
                                                sx={{ borderRadius: '15px' }}
                                                autoWidth
                                                label="Ubicación"
                                                value={category}
                                                onChange={handleCategoryChange}
                                                onBlur={handleCategoryBlur}
                                                error={categoryError}
                                            >
                                                {categories.map((cat: any) => (
                                                    <MenuItem value={cat.id}>{cat.name}</MenuItem>
                                                ))}
                                            </Select>
                                            {categoryError && <FormHelperText sx={{color: '#C61A24'}}>Este campo es obligatorio</FormHelperText>}
                                        </FormControl>
                                    </InputsContainer>
                                    <Button
                                        variant="contained"
                                        onClick={manageType}
                                        sx={{
                                            width: "100%",
                                            marginTop: "20px",
                                            borderRadius: "15px",
                                            fontSize: "16px",
                                            backgroundColor: theme.color.navy,
                                            color: theme.color.white,
                                            textTransform: "none",
                                            "&:hover": {
                                                backgroundColor: theme.color.space,
                                            },
                                        }}
                                    >
                                        Guardar
                                    </Button>
                                </>
                            )
                    }
                </Box>
            </Modal>
        </div>
    );
}

export default TypeActions;